import Alpine, {Bindings} from 'alpinejs';

const INTERACTIVE = 'a, button, [role="button"], input, select, textarea, label, summary, details';

/**
 * Makes a container element act as a clickable link area by delegating clicks
 * to a real <a> descendant. The <a> provides href/target and is required for
 * keyboard navigation, right-click context menu, and screen reader accessibility.
 * This directive only extends click coverage to non-interactive content (e.g.
 * plain text, price) that would otherwise swallow clicks without navigating.
 *
 * By default the first `a[href]` descendant is used. To specify a particular
 * link, add `x-link-area:anchor` to it.
 *
 * Usage (default):
 *   <div x-link-area>
 *     <a href="/path" class="...">...</a>
 *     <!-- nested buttons/links work normally -->
 *   </div>
 *
 * Usage (explicit anchor):
 *   <div x-link-area>
 *     <a x-link-area:anchor href="/path" class="...">...</a>
 *   </div>
 */
Alpine.directive('link-area', ($el, {value}) => {
  // x-link-area:anchor — marks this <a> as the designated target link.
  // The parent x-link-area container will prefer this over any other <a>.
  if (value === 'anchor') {
    ($el as HTMLElement).dataset.linkAreaAnchor = 'true';
    return;
  }

  // x-link-area — the container that delegates clicks.
  function findLink(): HTMLAnchorElement | null {
    return (
      $el.querySelector<HTMLAnchorElement>('[data-link-area-anchor]') ?? $el.querySelector<HTMLAnchorElement>('a[href]')
    );
  }

  // Alpine's Bindings type declares every value as `() => unknown`, but at runtime
  // `@event` handlers are called with the event. Assert at the boundary so the
  // handler bodies below keep their real parameter types.
  Alpine.bind($el, {
    '@click'(_e: Event) {
      const e = _e as MouseEvent;
      if ((e.target as Element).closest(INTERACTIVE)) return;
      if (window.getSelection()?.toString()) return;

      const link = findLink();
      if (!link) return;

      const {href, target} = link;
      const newTab = target === '_blank';

      if (e.metaKey || e.ctrlKey || newTab) {
        window.open(href, '_blank');
      } else if (e.shiftKey) {
        window.open(href);
      } else {
        window.location.href = href;
      }
    },

    '@auxclick'(_e: Event) {
      const e = _e as MouseEvent;
      if (e.button !== 1) return;
      if ((e.target as Element).closest(INTERACTIVE)) return;

      const link = findLink();
      if (!link) return;

      e.preventDefault();
      window.open(link.href, '_blank');
    },
  } as unknown as Bindings);
});
